import watch from 'redux-watch';
import {DefaultSettings} from "../../../../settings/settings-util";
import {Store} from "redux";
import {AlmeState} from "../../../../wireup/AlmeState";
import {Message} from "../../../../types/response";
import {AlmeResponseV5} from "@alme/types/response/AlmeResponse";
import {appendLiveChatHistoryMessage} from "../actions/actions";
import LiveChatState from "../livechat-state";


export default class HistoryAppender
{

    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _stateManager: LiveChatState;
    
    
    //#region Public Interface
    
    constructor(settings: DefaultSettings, stateManager: LiveChatState)
    {
        this._settings = settings;
        this._stateManager = stateManager;
    }

    initialize(store: Store<AlmeState>): void
    {
        this._store = store;

        const { Base, processedResponse } = this._settings.stateLocations.Conversation;
        const stateLocation = `${Base}.${processedResponse}`;

        const liveChatInputWatcher = watch(this._store.getState.bind(this), stateLocation);
        const liveChatResponseWatcher = watch(this._store.getState.bind(this), stateLocation);
        const connectBannerWatcher = watch(this._store.getState.bind(this), stateLocation);
        const queuedBannerWatcher = watch(this._store.getState.bind(this), stateLocation);
        const disconnectBannerWatcher = watch(this._store.getState.bind(this), stateLocation);

        this._store?.subscribe(liveChatInputWatcher((newVal: Message) => this.appendLiveChatInputToHistory(newVal)));
        this._store?.subscribe(liveChatResponseWatcher((newVal: Message) => this.appendLiveChatResponseToHistory(newVal)));
        this._store?.subscribe(connectBannerWatcher((newVal: Message) => this.appendConnectBannerToHistory(newVal)));
        this._store?.subscribe(queuedBannerWatcher((newVal: Message) => this.appendQueuedBannerToHistory(newVal)));
        this._store?.subscribe(disconnectBannerWatcher((newVal: Message) => this.appendDisconnectBannerToHistory(newVal)));
    }
    
    //#endregion
    
    
    
    //#region Append Methods

    appendLiveChatInputToHistory(message?: Message): void
    {
        const { livechatInputType } = this._settings.messageConstants;

        if (message?.error ||
            message?.messageType !== livechatInputType ||
            ((message?.response as AlmeResponseV5).maskedInput?.trim() ?? 0) <= 0)
        {
            return;
        }

        const { responseRevision, maskedInput, extensionData } = message.response as AlmeResponseV5;
        const historyMessage = {
            type: livechatInputType,
            responseRevision,
            maskedInput,
            extensionData,
        };

        this._store?.dispatch(appendLiveChatHistoryMessage(historyMessage));
    }

    appendLiveChatResponseToHistory(message?: Message): void
    {
        const { almeResponseType, livechatResponseType } = this._settings.messageConstants;

        if (message?.messageType !== livechatResponseType && message?.messageType !== almeResponseType) { return; }

        const { responseRevision, text, displayLinkCollection, extensionData, navUrl } = message?.response as AlmeResponseV5;
        const historyMessage =
        {
            type: message?.messageType,
            responseRevision,
            displayLinkCollection,
            text,
            extensionData,
            operatorData: this._stateManager.getLatestOperatorData(),
            navUrl,
        };

        this._store?.dispatch(appendLiveChatHistoryMessage(historyMessage));
    }

    appendConnectBannerToHistory(message?: Message): void
    {
        const { livechatConnectBanner } = this._settings.messageConstants;

        if (message?.messageType !== livechatConnectBanner) { return; }

        const operatorDataIndex = this._stateManager.getOperatorDataLength();
        const historyMessage = { type: livechatConnectBanner, operatorDataIndex };

        this._store?.dispatch(appendLiveChatHistoryMessage(historyMessage));
    }

    appendQueuedBannerToHistory(message?: Message): void
    {
        const { livechatQueuedBanner } = this._settings.messageConstants;

        if (message?.messageType !== livechatQueuedBanner) { return; }

        this._store?.dispatch(appendLiveChatHistoryMessage({ type: livechatQueuedBanner }));
    }

    appendDisconnectBannerToHistory(message?: Message): void
    {
        const { livechatDisconnectBanner } = this._settings.messageConstants;

        if (message?.messageType !== livechatDisconnectBanner) { return; }

        const { extensionData } = message?.response as AlmeResponseV5;
        const historyMessage = { type: livechatDisconnectBanner, extensionData };

        this._store?.dispatch(appendLiveChatHistoryMessage(historyMessage));
    }
    
    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/livechat-foundation/src/handlers/history-appender.ts